import {
  LayerData,
  landxmlLoadingAtom,
  layerRuntimeCentersAtom,
  selectedLandXMLFeatureAtom,
  tLayerSettings,
  type LandxmlLoadingEntry,
} from '@/atoms';
import { clipBoxStateAtom } from '@/atoms/viewer.atom';
import {
  alignmentDrapeSourcesAtom,
  alignmentsAtom,
  registerAlignmentsAtom,
  selectedAlignmentKeyAtom,
  unregisterAlignmentsAtom,
  type DrapeSampler,
} from '@/atoms/alignment.atom';
import {
  crestSourcesFromEntries,
  registerCrestSourcesAtom,
  unregisterCrestSourcesAtom,
} from '@/atoms/crestLine.atom';
import type { SelectedLandXMLFeature } from '@/atoms/landxmlSelection.atom';
import { isMeasurementToolActive, toolPickSuppressedAtom } from '@/atoms/pickSuppression.atom';
import { alignmentKey, buildAlignment, projectPoint, type Alignment } from '@/core/alignment';
import { getResourcesEndpoint } from '@/core/resources';
import {
  LandXMLCategory,
  LandXMLFeature,
  LandxmlNonMetreUnitsError,
  buildSurfaceSampler,
  parseAlignments,
  parseBreaklines,
  parseParcels,
  parsePipeNetworks,
  parseSurfaces,
} from '@/core/landxml';
import { extractTins } from '@/core/landxmlTin';
import { LOD_MAX_CELL_PX, buildTinLod, unitsPerPixelAt } from '@/core/landxmlLod';
import { useLayerSettingsMany } from '@/hooks/useLayerSettings';
import { sampleColormap } from '@/lib/colormaps';
import { applyClipBoxPlanesToMaterial, buildClipBoxPlanes, CLIP_BOX_HIGHLIGHT_DIM, clipBoxIsHighlight } from '@/lib/clipBoxPlanes';
import { getLayerTransform } from '@/lib/layerTransform';
import { toOfflineUrl, useServeOffline } from '@/lib/offline/offlineResourceBase';
import { useFrame, useThree, type ThreeEvent } from '@react-three/fiber';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import * as THREE from 'three';
import PickableGroup from './3D/PickableGroup';
import { landxmlEntryKey, planLandxmlLoads } from './landxmlLoadPlan';

/**
 * Per-layer entry: the parsed features (with their world-space origin offset)
 * plus a fast lookup of feature → metadata for the click handler.
 */
interface LayerEntry {
  /** `landxmlEntryKey(layerId, root)` — identity for the incremental loader. */
  key: string;
  layerId: string;
  origin: THREE.Vector3;
  features: LandXMLFeature[];
  /** Absolute-coordinate alignments (layer transform applied at registration time). */
  alignments: Alignment[];
  sampler: DrapeSampler | null;
}

const disposeEntries = (list: LayerEntry[]) => {
  for (const e of list) {
    for (const f of e.features) {
      f.geometry.dispose();
      f.lod?.geometry.dispose();
    }
  }
};

const FEATURE_ID_KEY = 'landxmlFeatureId';
const FEATURE_LAYER_KEY = 'landxmlLayerId';

/** Build a colored material from a single hex string. */
function makeSolidMaterial(hex: string, opacity: number, isWireframe: boolean, double = true) {
  return new THREE.MeshLambertMaterial({
    color: hex,
    side: double ? THREE.DoubleSide : THREE.FrontSide,
    wireframe: isWireframe,
    transparent: opacity < 1,
    opacity,
  });
}

/**
 * Vertex-color a TIN by elevation or slope, using the chosen colormap.
 * Mutates the geometry by attaching a 'color' attribute.
 *
 * For ELEVATION we recompute min/max from the geometry's current position
 * buffer rather than trusting any pre-shift metadata: the load effect
 * translates each geometry by `-finalOrigin`, so absolute Z values would
 * map to t≈0 and everything would render blue.
 */
function applyVertexColors(geometry: THREE.BufferGeometry, mode: 'ELEVATION' | 'SLOPE', colormap: string) {
  const pos = geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
  if (!pos) return;
  const count = pos.count;
  const colors = new Float32Array(count * 3);
  const c = new THREE.Color();
  if (mode === 'ELEVATION') {
    let zMin = Infinity;
    let zMax = -Infinity;
    for (let i = 0; i < count; i++) {
      const z = pos.getZ(i);
      if (z < zMin) zMin = z;
      if (z > zMax) zMax = z;
    }
    const range = Math.max(1e-9, zMax - zMin);
    for (let i = 0; i < count; i++) {
      const t = (pos.getZ(i) - zMin) / range;
      sampleColormap(colormap, t, c);
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }
  } else {
    // SLOPE: angle between face-normal and Z axis
    let normals = geometry.getAttribute('normal') as THREE.BufferAttribute | undefined;
    if (!normals) {
      geometry.computeVertexNormals();
      normals = geometry.getAttribute('normal') as THREE.BufferAttribute | undefined;
    }
    if (!normals) return;
    for (let i = 0; i < count; i++) {
      const nz = Math.min(1, Math.max(-1, normals.getZ(i)));
      const angle = Math.acos(Math.abs(nz)); // 0 = flat, π/2 = vertical
      const t = angle / (Math.PI / 2);
      sampleColormap(colormap, t, c);
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
}

/**
 * Pick the right hex color for a feature given the current layer settings.
 * Returns null when surfaces should be vertex-colored instead.
 */
function colorForCategory(category: LandXMLCategory, settings: tLayerSettings): string {
  const colors = settings.landxmlCategoryColors;
  switch (category) {
    case 'SURFACE':
      return colors?.surface ?? settings.modelDefaultColor;
    case 'ALIGNMENT':
      return colors?.alignment ?? '#e53935';
    case 'PARCEL':
      return colors?.parcel ?? '#43a047';
    case 'BREAKLINE':
      return colors?.breakline ?? '#787878';
    case 'PIPE':
      return colors?.pipe ?? '#8b6914';
    case 'STRUCTURE':
      return colors?.structure ?? '#607d8b';
  }
}

/**
 * One TIN surface, drawn at whichever level of detail the current zoom can
 * resolve.
 *
 * The swap is imperative rather than declarative on purpose: it is a
 * per-frame decision, and routing it through React state would re-render a
 * component that owns multi-million-vertex buffers every time the user
 * scrolls the wheel.
 */
const LodSurface = ({
  feature,
  material,
  onDoubleClick,
}: {
  feature: LandXMLFeature;
  material: THREE.Material;
  onDoubleClick: (e: ThreeEvent<MouseEvent>) => void;
}) => {
  const ref = useRef<THREE.Mesh>(null);
  const centre = useRef(new THREE.Vector3());
  const full = feature.geometry;
  const lod = feature.lod;

  // Picking must never hit the coarse level. The measuring, draping and
  // elevation-profile tools all read a Z back out of the hit point, and on a
  // clustered level that Z is a cell average, not a surveyed vertex — so
  // raycast the full TIN no matter which one is on screen.
  useEffect(() => {
    const mesh = ref.current;
    if (!mesh || !lod) return;
    mesh.raycast = function raycastFullResolution(this: THREE.Mesh, raycaster, intersects) {
      const drawn = this.geometry;
      this.geometry = full;
      try {
        THREE.Mesh.prototype.raycast.call(this, raycaster, intersects);
      } finally {
        this.geometry = drawn;
      }
    };
    return () => {
      mesh.raycast = THREE.Mesh.prototype.raycast;
    };
  }, [full, lod]);

  useFrame(({ camera, size }) => {
    const mesh = ref.current;
    if (!mesh || !lod) return;
    if (!full.boundingSphere) full.computeBoundingSphere();
    const sphere = full.boundingSphere;
    if (!sphere) return;
    centre.current.copy(sphere.center).applyMatrix4(mesh.matrixWorld);
    const unitsPerPixel = unitsPerPixelAt(camera, centre.current, size.height);
    const coarse = unitsPerPixel > 0 && lod.cellSize / unitsPerPixel < LOD_MAX_CELL_PX;
    const wanted = coarse ? lod.geometry : full;
    if (mesh.geometry !== wanted) mesh.geometry = wanted;
  });

  return <mesh ref={ref} geometry={full} material={material} onDoubleClick={onDoubleClick} />;
};

export const LandXML = ({ layers }: { layers: LayerData[] }) => {
  const [entries, setEntries] = useState<LayerEntry[]>([]);
  // Mirror of `entries` for the disposal cleanups below. They must dispose
  // exactly what is LIVE at the moment they run — a cleanup that closed over a
  // stale `entries` would either miss geometries or (worse) dispose ones the
  // next array still renders. Kept in an effect, never assigned during render.
  const entriesRef = useRef<LayerEntry[]>([]);
  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);
  const { t } = useTranslation('project');
  const layerSettings = useLayerSettingsMany(layers.map((l) => l.id));
  const setSelected = useSetAtom(selectedLandXMLFeatureAtom);
  const setSelectedAlignmentKey = useSetAtom(selectedAlignmentKeyAtom);
  // The click handler reads the alignment registry imperatively — see _handleDoubleClick.
  const store = useStore();
  const raycaster = useThree((s) => s.raycaster);
  const gl = useThree((s) => s.gl);
  const { projectId } = useParams<{ projectId: string }>();
  const clipBoxState = useAtomValue(clipBoxStateAtom(projectId!));
  const setRuntimeCenters = useSetAtom(layerRuntimeCentersAtom);
  const setLoading = useSetAtom(landxmlLoadingAtom);
  // Clip box → Three.js clipping planes, applied to every material built in
  // `materialsByLayer` below (surfaces, pipes, structures and line features).
  const clip = useMemo(() => buildClipBoxPlanes(clipBoxState), [clipBoxState]);
  const isHighlight = clipBoxIsHighlight(clipBoxState);
  useEffect(() => {
    if (clip) gl.localClippingEnabled = true;
  }, [clip, gl]);
  const serveOffline = useServeOffline(projectId);

  // Three.js' default Line raycast threshold is 1 world-unit — far too tight
  // for civil-scale alignments, where a typical zoom level makes that a
  // single-pixel hitbox. Bump it while LandXML is mounted so clicking
  // a thin alignment/parcel/breakline actually registers.
  useEffect(() => {
    if (!raycaster.params.Line) return;
    const prev = raycaster.params.Line.threshold;
    raycaster.params.Line.threshold = Math.max(prev, 3);
    return () => {
      raycaster.params.Line!.threshold = prev;
    };
  }, [raycaster]);

  // Feature registry indexed by `${layerId}:${featureId}` so the click
  // handler can recover full metadata from a raycast hit's userData.
  const registryRef = useRef<Map<string, LandXMLFeature & { layerId: string }>>(new Map());

  // Identity of the desired layer set. Only its VALUE matters — an unrelated
  // re-render (settings, colours, clip box) produces the same string and the
  // effect below is skipped.
  const layerLoadKey = useMemo(() => layers.map((l) => `${l.id}:${l.root}`).join(','), [layers]);

  /**
   * In-flight loads, keyed by `landxmlEntryKey`, and the sequential queue that
   * drains them. Both live in refs, NOT in the effect: the whole point of the
   * incremental loader is that a layer-set change must not disturb a load that
   * is still wanted. An effect-scoped AbortController (the previous design)
   * aborted every fetch on any change, which is what made toggling one layer
   * re-download all the others.
   */
  const inFlightRef = useRef<Map<string, AbortController>>(new Map());
  const queueRef = useRef<{ key: string; layerId: string; root: string; layerName: string }[]>([]);
  const drainingRef = useRef(false);

  // --- Parse + load LandXML files ----------------------------------------
  useEffect(() => {
    /** Merge a patch into this layer's progress badge. */
    const reportLoading = (entry: LandxmlLoadingEntry) =>
      setLoading((prev) => ({ ...prev, [entry.layerId]: { ...prev[entry.layerId], ...entry } }));
    /** Drop the badge — the layer's geometry is on screen. */
    const clearLoading = (layerId: string) =>
      setLoading((prev) => {
        if (!prev[layerId]) return prev;
        const next = { ...prev };
        delete next[layerId];
        return next;
      });

    const loadOne = async (
      key: string,
      file: string,
      layerId: string,
      layerName: string,
      signal: AbortSignal,
    ) => {
      const t0 = performance.now();
      const since = (from: number) => `${((performance.now() - from) / 1000).toFixed(1)}s`;
      try {
        const httpsUrl = `${getResourcesEndpoint()}/${file}`;
        const url = serveOffline ? toOfflineUrl(httpsUrl) : httpsUrl;
        reportLoading({ layerId, layerName, phase: 'fetching' });
        const response = await fetch(url, {
          signal,
          ...(serveOffline ? {} : { credentials: 'include' as const }),
        });
        // A non-2xx response still has a body — an S3/CloudFront XML error
        // document, which DOMParser happily parses into a well-formed doc with
        // no <Surface> in it. Without this guard the layer renders nothing and
        // reports no error at all.
        if (!response.ok) {
          console.error(`LandXML fetch failed: ${response.status} ${response.statusText} for ${file}`);
          reportLoading({ layerId, layerName, phase: 'failed', error: `HTTP ${response.status}` });
          return;
        }

        // Read the body as a stream so the badge can show real progress.
        // `.text()` on a 200 MB payload is a single await that reports nothing
        // for tens of seconds — which is exactly what made a working layer look
        // like a broken one.
        const totalBytes = Number(response.headers.get('content-length')) || undefined;
        const fetchStart = performance.now();
        let text: string;
        if (response.body) {
          const reader = response.body.getReader();
          const decoder = new TextDecoder('utf-8');
          const parts: string[] = [];
          let received = 0;
          let lastTick = 0;
          for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            received += value.byteLength;
            parts.push(decoder.decode(value, { stream: true }));
            // Throttled: a per-chunk atom write would re-render the overlay
            // hundreds of times per second for no extra information.
            const now = performance.now();
            if (now - lastTick > 150) {
              lastTick = now;
              reportLoading({
                layerId,
                layerName,
                phase: 'fetching',
                loadedBytes: received,
                totalBytes,
                progress: totalBytes ? received / totalBytes : undefined,
              });
            }
          }
          parts.push(decoder.decode());
          text = parts.join('');
        } else {
          text = await response.text();
        }
        const fetchMs = performance.now() - fetchStart;
        if (signal.aborted) return;
        reportLoading({ layerId, layerName, phase: 'decoding', loadedBytes: text.length, totalBytes });
        // Yield once so the badge repaints as "decoding" before the parse takes
        // the main thread.
        await new Promise((r) => setTimeout(r, 0));
        if (signal.aborted) return;

        // Pull the TIN blocks out of the raw text FIRST. They are ~99% of a
        // DTM export's bytes, and handing them to DOMParser is what made these
        // files unusable — see landxmlTin.ts. What is left parses instantly.
        const parseStart = performance.now();
        const { lightText, tins } = extractTins(text);
        const parser = new DOMParser();
        const doc = parser.parseFromString(lightText, 'application/xml');
        const parserError = doc.querySelector('parsererror');
        if (parserError) {
          console.error('LandXML parse error:', parserError.textContent);
          reportLoading({ layerId, layerName, phase: 'failed', error: 'Malformed XML' });
          return;
        }

        const surfaces = parseSurfaces(doc, tins);
        const breaklines = parseBreaklines(doc);
        const parcels = parseParcels(doc);
        const pipeNetworks = parsePipeNetworks(doc);
        // Snapshots ABSOLUTE surface coordinates → must be taken before the
        // origin shift below. The returned sampler defers its (expensive) grid
        // build to the first query, so a layer nothing ever drapes onto pays
        // almost nothing here.
        const sampler: DrapeSampler | null = surfaces.length ? buildSurfaceSampler(surfaces) : null;
        const alignments = parseAlignments(doc, { drape: sampler });
        const parseMs = performance.now() - parseStart;
        console.info(
          `[LandXML] ${layerName}: ${(text.length / 1048576).toFixed(0)} MB, ` +
            `${surfaces.reduce((n, s) => n + (s.metadata.triangleCount ?? 0), 0).toLocaleString()} triangles ` +
            `in ${since(t0)} (fetch ${(fetchMs / 1000).toFixed(1)}s, parse ${(parseMs / 1000).toFixed(1)}s)`,
        );

        // Compute origin from first available vertex (any feature).
        let origin: THREE.Vector3 | null = null;
        const all: LandXMLFeature[] = [
          ...surfaces,
          ...alignments,
          ...parcels,
          ...pipeNetworks.lines,
          ...pipeNetworks.meshes,
          ...breaklines,
        ];
        for (const f of all) {
          const pos = f.geometry.getAttribute('position');
          if (pos && pos.count > 0) {
            origin = new THREE.Vector3(pos.getX(0), pos.getY(0), pos.getZ(0));
            break;
          }
        }
        const finalOrigin = origin ?? new THREE.Vector3();

        // Absolute bbox centre → fallback for "Jump to layer center" when the
        // layer row has no stored `center`. Taken before the origin shift.
        const bbox = new THREE.Box3();
        for (const f of all) {
          f.geometry.computeBoundingBox();
          if (f.geometry.boundingBox) bbox.union(f.geometry.boundingBox);
        }

        // Offset every geometry to world-relative space so the parent group
        // can position the whole model at `origin`.
        for (const f of all) {
          f.geometry.translate(-finalOrigin.x, -finalOrigin.y, -finalOrigin.z);
        }

        if (signal.aborted) return;

        // Tag each feature with a stable `${category}:${name}:${ordinal}` id
        // and register it. Alignments additionally become registry entries
        // (still in absolute coords — the layer transform is applied when
        // they're published, so settings changes don't need a re-parse).
        const ordinals = new Map<string, number>();
        const features: LandXMLFeature[] = [];
        const builtAlignments: Alignment[] = [];
        for (const f of all) {
          const base = `${f.category}:${f.name}`;
          const ord = ordinals.get(base) ?? 0;
          ordinals.set(base, ord + 1);
          const featureId = `${base}:${ord}`;
          f.geometry.userData = {
            ...(f.geometry.userData ?? {}),
            [FEATURE_ID_KEY]: featureId,
            [FEATURE_LAYER_KEY]: layerId,
          };
          features.push(f);
          registryRef.current.set(`${layerId}:${featureId}`, { ...f, layerId });
          if (f.category === 'ALIGNMENT' && f.metadata.alignment) {
            const a = buildAlignment({
              key: alignmentKey(layerId, featureId),
              source: 'LANDXML',
              layerId,
              featureId,
              name: f.name,
              points: f.metadata.alignment.points,
              staStart: f.metadata.alignment.staStart,
              equations: f.metadata.alignment.equations,
              keyPoints: f.metadata.alignment.keyPoints,
              // Chord fallbacks the parser had to make (corridor-trust T6c) —
              // the UI reads them off the registered alignment.
              degradations: f.metadata.alignment.degradations,
            });
            if (a) builtAlignments.push(a);
          }
        }

        // Coarse display level per surface. Built here, after the origin shift
        // and the userData tagging, so the level shares both with the geometry
        // it stands in for. Costs a few hundred ms on a multi-million-triangle
        // TIN — paid once, inside the load the badge is already showing.
        const lodStart = performance.now();
        let lodTriangles = 0;
        for (const f of features) {
          if (f.category !== 'SURFACE') continue;
          f.lod = buildTinLod(f.geometry) ?? undefined;
          if (f.lod) lodTriangles += f.lod.geometry.getIndex()!.count / 3;
        }
        if (lodTriangles > 0) {
          console.info(
            `[LandXML] ${layerName}: LOD ${lodTriangles.toLocaleString()} triangles ` +
              `in ${Math.round(performance.now() - lodStart)}ms`,
          );
        }

        if (!bbox.isEmpty()) {
          const c = bbox.getCenter(new THREE.Vector3());
          setRuntimeCenters((prev) => ({ ...prev, [layerId]: [c.x, c.y, c.z] }));
        }

        setEntries((prev) => [
          ...prev,
          { key, layerId, origin: finalOrigin, features, alignments: builtAlignments, sampler },
        ]);
        clearLoading(layerId);
      } catch (err) {
        if (signal.aborted) return;
        console.error('Error loading LandXML:', err);
        // A non-metre file is a REFUSAL, not a crash: the parsers decline to
        // render coordinates that are not in metres (see `assertMetreUnits`).
        // Say so in the user's language, in a toast — the badge alone reads
        // like a broken download, and the console line reaches nobody.
        if (err instanceof LandxmlNonMetreUnitsError) {
          const message = t('landxml.errors.nonMetreUnits', { unit: err.unit });
          toast.error(message);
          reportLoading({ layerId, layerName, phase: 'failed', error: message });
          return;
        }
        reportLoading({ layerId, layerName, phase: 'failed', error: err instanceof Error ? err.message : String(err) });
      }
    };

    // What changed since the last run. Everything already parsed, or in flight
    // for a still-selected layer, is left strictly alone — see landxmlLoadPlan.
    const plan = planLandxmlLoads(
      layers,
      entriesRef.current.map((e) => e.key),
      inFlightRef.current.keys(),
    );

    // 1. Abort loads for layers that are no longer selected.
    for (const key of plan.toAbort) {
      inFlightRef.current.get(key)?.abort();
      inFlightRef.current.delete(key);
    }
    if (plan.toAbort.length > 0) {
      const aborted = new Set(plan.toAbort);
      queueRef.current = queueRef.current.filter((q) => !aborted.has(q.key));
    }

    // 2. Dispose entries whose layer left, and ONLY those. `entriesRef` is
    //    updated in the same breath, so a repeated run (StrictMode's double
    //    invoke) cannot dispose the same geometries twice.
    if (plan.toDispose.length > 0) {
      const dropped = new Set(plan.toDispose);
      const gone = entriesRef.current.filter((e) => dropped.has(e.key));
      const kept = entriesRef.current.filter((e) => !dropped.has(e.key));
      for (const e of gone) {
        for (const f of e.features) {
          const featureId = (f.geometry.userData ?? {})[FEATURE_ID_KEY];
          if (featureId) registryRef.current.delete(`${e.layerId}:${featureId}`);
        }
      }
      disposeEntries(gone);
      entriesRef.current = kept;
      setEntries(kept);
    }

    // 3. Drop the badges of layers that left mid-load or mid-queue — an
    //    aborted load is not a failed one.
    const departed = [...plan.toAbort, ...plan.toDispose];
    if (departed.length > 0) {
      setLoading((prev) => {
        const next = { ...prev };
        for (const key of departed) delete next[key.split('\u0000')[0]];
        return next;
      });
    }

    // 4. Queue the newly selected files. SEQUENTIALLY, not `layers.forEach`:
    //    each can be 200 MB on the wire and hundreds of MB of transient parse
    //    state; starting eleven at once meant ~1.4 GB of raw strings before the
    //    first vertex existed, which took the tab down. One at a time also lets
    //    surfaces appear progressively. The queue outlives this effect, so a
    //    toggle during a load appends to it instead of restarting everything.
    for (const target of plan.toLoad) {
      inFlightRef.current.set(target.key, new AbortController());
      queueRef.current.push(target);
      reportLoading({ layerId: target.layerId, layerName: target.layerName, phase: 'fetching' });
    }

    if (!drainingRef.current && queueRef.current.length > 0) {
      drainingRef.current = true;
      void (async () => {
        try {
          while (queueRef.current.length > 0) {
            const next = queueRef.current.shift()!;
            const controller = inFlightRef.current.get(next.key);
            // Switched off again between queueing and dequeueing.
            if (!controller || controller.signal.aborted) continue;
            await loadOne(next.key, next.root, next.layerId, next.layerName, controller.signal);
            // Only retire OUR controller: if the layer was switched off and on
            // again during the load, the map already holds the replacement and
            // deleting it would strand the re-queued entry — it would be
            // dequeued with no controller and skipped forever.
            if (inFlightRef.current.get(next.key) === controller) inFlightRef.current.delete(next.key);
          }
        } finally {
          drainingRef.current = false;
        }
      })();
    }

    // No cleanup function: this effect owns no per-run resources. Aborting and
    // disposing on every run is precisely the bug this replaces — unmount
    // disposal has its own effect below.
    // We intentionally key off layerLoadKey, not the in-flight `entries` array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerLoadKey, serveOffline]);

  // --- Build materials per (layer, category) -----------------------------
  // Re-creates whenever color settings or the highlight state change. Surface
  // materials are special when the user picks ELEVATION / SLOPE — they use
  // vertexColors. HIGHLIGHT_INSIDE dims everything (the orange box overlay
  // itself is drawn once by IFC2, which is always mounted).
  const materialsByLayer = useMemo(() => {
    const map = new Map<string, Record<LandXMLCategory, THREE.Material> & { surfaceVertexColored?: THREE.Material }>();
    const dim = isHighlight ? CLIP_BOX_HIGHLIGHT_DIM : 1.0;
    for (const e of entries) {
      const s = layerSettings[e.layerId];
      if (!s) continue;
      const opacity = (s.modelOpacity ?? 1.0) * dim;
      const isWireframe = s.modelIsWireframe;
      const surfaceMode = s.landxmlSurfaceColorMode ?? 'SOLID';
      const lineOpts = { transparent: dim < 1, opacity: dim };

      const cats: Record<LandXMLCategory, THREE.Material> = {
        SURFACE: makeSolidMaterial(colorForCategory('SURFACE', s), opacity, isWireframe),
        ALIGNMENT: new THREE.LineBasicMaterial({ color: colorForCategory('ALIGNMENT', s), ...lineOpts }),
        PARCEL: new THREE.LineBasicMaterial({ color: colorForCategory('PARCEL', s), ...lineOpts }),
        BREAKLINE: new THREE.LineBasicMaterial({ color: colorForCategory('BREAKLINE', s), ...lineOpts }),
        PIPE: makeSolidMaterial(colorForCategory('PIPE', s), opacity, isWireframe),
        STRUCTURE: makeSolidMaterial(colorForCategory('STRUCTURE', s), opacity, isWireframe),
      };
      const surfaceVertexColored =
        surfaceMode !== 'SOLID'
          ? new THREE.MeshLambertMaterial({
              vertexColors: true,
              side: THREE.DoubleSide,
              wireframe: isWireframe,
              transparent: opacity < 1,
              opacity,
            })
          : undefined;
      map.set(e.layerId, { ...cats, surfaceVertexColored });
    }
    return map;
  }, [entries, layerSettings, isHighlight]);

  // Clip planes are assigned in place rather than baked into the memo above:
  // the box changes every frame while dragging, and rebuilding every
  // material per frame would be wasteful (and they're never disposed).
  useEffect(() => {
    for (const mats of materialsByLayer.values()) {
      for (const mat of Object.values(mats)) if (mat) applyClipBoxPlanesToMaterial(mat, clip);
    }
  }, [materialsByLayer, clip]);

  // --- Publish alignments + drape sources to the registry -----------------
  const registerAlignments = useSetAtom(registerAlignmentsAtom);
  const unregisterAlignments = useSetAtom(unregisterAlignmentsAtom);
  const setDrapeSources = useSetAtom(alignmentDrapeSourcesAtom);
  const registerCrestSources = useSetAtom(registerCrestSourcesAtom);
  const unregisterCrestSources = useSetAtom(unregisterCrestSourcesAtom);
  // What this component currently owns in the registry. Registration REPLACES
  // an owner's whole set, so the effect below deliberately has no per-change
  // cleanup: tearing everything down and re-adding it on each `entries` /
  // `layerSettings` change would momentarily leave the walked (or selected)
  // key absent, and `registerAlignmentsAtom` cancels the walk when that
  // happens — an opacity tweak or a second layer finishing its load would
  // silently end an in-progress walk. Only owners that actually vanish are
  // unregistered here; a dedicated unmount effect clears the rest.
  const registeredRef = useRef<Map<string, { source: Alignment[]; transformKey: string }>>(new Map());
  const drapeOwnersRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const nextOwners = new Map<string, { source: Alignment[]; transformKey: string }>();
    const nextDrapes = new Map<string, DrapeSampler>();
    for (const e of entries) {
      const s = layerSettings[e.layerId];
      const transform = getLayerTransform(s);
      const transformKey = `${transform.offset.join(',')}|${transform.rotation.join(',')}`;
      nextOwners.set(e.layerId, { source: e.alignments, transformKey });
      // Samplers read raw file coordinates — only valid while untransformed.
      if (e.sampler && transform.isIdentity) nextDrapes.set(e.layerId, e.sampler);

      // Skip the write when neither the parsed alignments nor the transform
      // moved — keeps unrelated settings changes from churning the registry.
      const prev = registeredRef.current.get(e.layerId);
      if (prev && prev.source === e.alignments && prev.transformKey === transformKey) continue;

      // world = R·(p − origin) + origin + offset  (mirrors the PickableGroup below)
      const toWorld = (a: Alignment): Alignment => {
        if (transform.isIdentity) return a;
        const pts = new Float64Array(a.points);
        const v = new THREE.Vector3();
        for (let i = 0; i < pts.length; i += 3) {
          v.set(pts[i] - e.origin.x, pts[i + 1] - e.origin.y, pts[i + 2] - e.origin.z)
            .applyQuaternion(transform.quaternion)
            .add(e.origin)
            .add(transform.offsetVec);
          pts[i] = v.x;
          pts[i + 1] = v.y;
          pts[i + 2] = v.z;
        }
        // buildAlignment re-adds START/END, so strip the synthetic ones first.
        return (
          buildAlignment({
            ...a,
            points: pts,
            keyPoints: a.keyPoints.filter((k) => k.kind !== 'START' && k.kind !== 'END'),
          }) ?? a
        );
      };
      registerAlignments({ ownerKey: e.layerId, alignments: e.alignments.map(toWorld) });
    }

    for (const owner of registeredRef.current.keys()) {
      if (!nextOwners.has(owner)) unregisterAlignments(owner);
    }
    registeredRef.current = nextOwners;

    const gone: string[] = [];
    for (const owner of drapeOwnersRef.current) if (!nextDrapes.has(owner)) gone.push(owner);
    drapeOwnersRef.current = new Set(nextDrapes.keys());
    setDrapeSources((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const o of gone) if (next.delete(o)) changed = true;
      for (const [k, v] of nextDrapes)
        if (next.get(k) !== v) {
          next.set(k, v);
          changed = true;
        }
      return changed ? next : prev;
    });

    // Crest extraction reuses the same identity-transform guard as the drape
    // sources above — samplers read raw file coordinates (see helper doc).
    const layerNameById = new Map(layers.map((l) => [l.id, l.name]));
    registerCrestSources({
      ownerKey: 'landxml',
      sources: crestSourcesFromEntries(
        entries.map((e) => ({
          layerId: e.layerId,
          layerName: layerNameById.get(e.layerId) ?? e.layerId,
          sampler: e.sampler,
          // `buildSurfaceSampler` combines EVERY <Surface> in the file and
          // answers with the first one that covers the query point, so the
          // names must be listed in the same order the parser produced them
          // — `features` preserves it (`all` is built surfaces-first).
          surfaceNames: e.features.filter((f) => f.category === 'SURFACE').map((f) => f.name),
        })),
        (id) => getLayerTransform(layerSettings[id]).isIdentity,
      ),
    });
  }, [entries, layerSettings, layers, registerAlignments, unregisterAlignments, setDrapeSources, registerCrestSources]);

  // Release everything this component owns — on unmount only.
  useEffect(() => {
    return () => {
      for (const owner of registeredRef.current.keys()) unregisterAlignments(owner);
      registeredRef.current = new Map();
      const owners = drapeOwnersRef.current;
      drapeOwnersRef.current = new Set();
      if (owners.size) {
        setDrapeSources((prev) => {
          const next = new Map(prev);
          for (const o of owners) next.delete(o);
          return next;
        });
      }
      unregisterCrestSources('landxml');
    };
    // Unmount-only: the registration effect above handles incremental changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply or strip vertex colors on surface geometries when mode/colormap changes.
  useEffect(() => {
    for (const e of entries) {
      const s = layerSettings[e.layerId];
      if (!s) continue;
      const mode = s.landxmlSurfaceColorMode ?? 'SOLID';
      const colormap = s.landxmlSurfaceColormap ?? 'SPECTRAL';
      for (const f of e.features) {
        if (f.category !== 'SURFACE') continue;
        // Both levels, or the mesh loses its colours the moment the LOD swaps.
        for (const g of [f.geometry, f.lod?.geometry]) {
          if (!g) continue;
          if (mode === 'SOLID') {
            g.deleteAttribute('color');
          } else {
            applyVertexColors(g, mode as 'ELEVATION' | 'SLOPE', colormap);
          }
        }
      }
    }
  }, [entries, layerSettings]);

  // Cleanup geometries on unmount ONLY. Keying this on `entries` disposed the
  // previous array's geometries whenever a second layer finished loading and
  // appended to it — but the new array still contains (and renders) those very
  // entries, so the first layer went blank.
  useEffect(() => {
    return () => {
      // The load effect no longer aborts per run (that WAS the bug), so the
      // only place an in-flight fetch is cancelled is here. Without it a
      // 200 MB download would keep going after the viewer closed.
      for (const controller of inFlightRef.current.values()) controller.abort();
      inFlightRef.current.clear();
      queueRef.current = [];
      disposeEntries(entriesRef.current);
      entriesRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // r3f DOUBLE-click handler at the group level — recovers the picked feature
  // from the intersected object's geometry.userData. The hit point is reported
  // in WORLD space; registered alignments live in world space too, so station /
  // offset are computed directly against the registry.
  //
  // Double-click rather than click for the same reason IFC2's element pick is:
  // a single click over a surface is the scene's most overloaded gesture (every
  // picking tool consumes one), and a surface covers most of the viewport, so
  // single-click select opened this drawer constantly by accident.
  const _handleDoubleClick = (e: {
    stopPropagation: () => void;
    object: THREE.Object3D;
    point: THREE.Vector3;
    /** r3f: pointer travel (px) since pointer-down — a camera orbit/pan ends with a large delta. */
    delta: number;
  }) => {
    // Same guard as GeoJson.tsx: ignore "clicks" that were really a drag.
    if (e.delta > 3) return;
    // …and, on exactly the same footing, ignore clicks that belong to a
    // drawing tool (Add Line / Polygon / Surface / Point, elevation profile,
    // stockpile, pipe, crest draw-guide). Those pick vertices off a DOM-level
    // `click`/`dblclick` on the very same canvas, so without this the one
    // gesture both drops a vertex AND pops this drawer open. Read imperatively
    // via `store` (not `useAtomValue`) for the same reason the alignment
    // registry below is: this handler must not re-subscribe the component.
    if (store.get(toolPickSuppressedAtom)) return;
    // The measuring tools are URL-driven, so they sit outside that atom.
    if (isMeasurementToolActive()) return;
    const obj = e.object as THREE.Mesh | THREE.Line;
    const ud = (obj.geometry as THREE.BufferGeometry | undefined)?.userData ?? {};
    const featureId = ud[FEATURE_ID_KEY];
    const layerId = ud[FEATURE_LAYER_KEY];
    if (!featureId || !layerId) return;
    const feat = registryRef.current.get(`${layerId}:${featureId}`);
    if (!feat) return;
    e.stopPropagation();

    const world = e.point.clone();
    let nearestAlignment: SelectedLandXMLFeature['nearestAlignment'];
    if (feat.category === 'ALIGNMENT') {
      setSelectedAlignmentKey(alignmentKey(layerId, featureId));
    } else {
      setSelectedAlignmentKey(null);
      // Read the registry imperatively — subscribing this component to the
      // registry it also writes to would re-render it on every registration.
      for (const a of store.get(alignmentsAtom).values()) {
        const r = projectPoint(a, world.x, world.y, world.z);
        if (!r) continue;
        if (!nearestAlignment || Math.abs(r.offset) < Math.abs(nearestAlignment.offset)) {
          nearestAlignment = { key: a.key, name: a.name, station: r.station, offset: r.offset, dz: r.dz };
        }
      }
    }

    setSelected({
      layerId,
      featureId,
      name: feat.name,
      category: feat.category,
      metadata: feat.metadata,
      clickPoint: { x: world.x, y: world.y, z: world.z },
      nearestAlignment,
    });
  };

  return (
    <>
      {entries
        .filter((e) => !!layerSettings[e.layerId])
        .map((e) => {
          const mats = materialsByLayer.get(e.layerId);
          if (!mats) return null;
          const surfaceMode = layerSettings[e.layerId]?.landxmlSurfaceColorMode ?? 'SOLID';
          // Per-layer transform: stack the user offset on top of the
          // load-time origin shift, and apply the user rotation as the
          // group's Euler tuple.
          const transform = getLayerTransform(layerSettings[e.layerId]);
          const groupPosition = transform.hasOffset ? e.origin.clone().add(transform.offsetVec) : e.origin;
          return (
            <PickableGroup key={e.layerId} position={groupPosition} rotation={transform.rotation}>
              {e.features.map((f) => {
                const isLine = f.category === 'ALIGNMENT' || f.category === 'PARCEL' || f.category === 'BREAKLINE';
                if (isLine) {
                  // r3f maps lowercase `line` to SVG in TS. Use a typed THREE.Line via primitive.
                  const lineObj = new THREE.Line(f.geometry, mats[f.category]);
                  lineObj.userData = {
                    [FEATURE_ID_KEY]: (f.geometry.userData as any)?.[FEATURE_ID_KEY],
                    [FEATURE_LAYER_KEY]: e.layerId,
                  };
                  return (
                    <primitive
                      key={`${e.layerId}-${(f.geometry.userData as any)?.[FEATURE_ID_KEY]}`}
                      object={lineObj}
                      onDoubleClick={_handleDoubleClick}
                    />
                  );
                }
                const useVertexColored =
                  f.category === 'SURFACE' && surfaceMode !== 'SOLID' && mats.surfaceVertexColored;
                const mat = useVertexColored ? mats.surfaceVertexColored! : mats[f.category];
                return (
                  <LodSurface
                    key={`${e.layerId}-${(f.geometry.userData as any)?.[FEATURE_ID_KEY]}`}
                    feature={f}
                    material={mat}
                    onDoubleClick={_handleDoubleClick}
                  />
                );
              })}
            </PickableGroup>
          );
        })}
    </>
  );
};
